// Client-side wrappers for the case-approval RPCs (see migration
// case_approval_flow_rpcs, 2026-05-14). One function per RPC plus a
// couple of read helpers the admin approval portal + client billing
// tab will share.
//
// All write helpers throw on error so call-sites can use try/catch +
// surface the message to the user. Read helpers return null on miss so
// the UI can render an empty state without branching on error.

import { supabase } from "./supabase";
import type {
  Competition,
  CompetitionStatus,
  CompetitionTier,
  PricingTier,
  Quote,
} from "../types/database";

// ============================================================================
// State-machine helpers
// ============================================================================

/** Status set during which a case is awaiting Coravu (read-only to the client). */
export const PENDING_APPROVAL_STATUSES = new Set<CompetitionStatus>([
  "submitted",
  "under_review",
]);

/** Status set during which a case has been approved but isn't live yet
 *  (contract + payment workflow before status -> open). */
export const PRE_ACTIVE_STATUSES = new Set<CompetitionStatus>([
  "approved",
  "contract_pending",
  "contract_signed",
  "payment_pending",
]);

/** Statuses that lock the case editor (no client edits allowed). */
export const EDITOR_LOCKED_STATUSES = new Set<CompetitionStatus>([
  "submitted",
  "under_review",
  "approved",
  "contract_pending",
  "contract_signed",
  "payment_pending",
  "open",
  "judging",
  "closed",
  "final_invoice_pending",
  "final_invoice_paid",
]);

export function canClientSubmit(status: CompetitionStatus): boolean {
  return status === "draft" || status === "denied";
}

// ============================================================================
// Quote inputs
// ============================================================================

export type ApproveCaseInput = {
  competitionId: string;
  /** 0-100. Super_admin only — RPC re-validates server-side. */
  manualDiscountPercent?: number;
  /** Whole cents. Defaults to 0 (no scholarship). */
  scholarshipAmountCents?: number;
  notesForClient?: string | null;
  /** Optional tier override. When provided, the RPC validates it and
   *  writes it back to competitions.tier as part of approval. When
   *  omitted, falls back to whatever's already set on the row. */
  tier?: CompetitionTier | null;
  /** Promo code to apply at approval time. Validated server-side; rejected
   *  codes raise with a human-readable error. Discount is baked into the
   *  quote's `initial_payment_cents` + recorded in `promo_redemptions`. */
  promoCode?: string | null;
};

// ============================================================================
// Write wrappers (one per RPC). Each throws with the RPC's error message.
// ============================================================================

export async function submitForReview(competitionId: string): Promise<void> {
  const { error } = await supabase.rpc("submit_case_for_review", {
    p_competition_id: competitionId,
  });
  if (error) throw new Error(error.message);
}

/**
 * Admin-on-behalf-of: create a Coravu-Managed (RM) case for an existing
 * client of an existing company. Returns the new case UUID. The RPC
 * enforces super_admin / admin_rm role. created_by points at the CLIENT
 * (so portal reads work) and created_by_admin tracks who actually set
 * it up. See migration admin_rm_case_intake.
 */
export async function adminCreateRmCase(
  clientUserId: string,
  companyId: string,
): Promise<string> {
  const { data, error } = await supabase.rpc("admin_create_rm_case", {
    p_client_user_id: clientUserId,
    p_company_id: companyId,
  });
  if (error) throw new Error(error.message);
  if (typeof data !== "string") {
    throw new Error("admin_create_rm_case did not return a case id");
  }
  return data;
}

export async function beginCaseReview(competitionId: string): Promise<void> {
  const { error } = await supabase.rpc("begin_case_review", {
    p_competition_id: competitionId,
  });
  if (error) throw new Error(error.message);
}

/** Creates a quote + transitions the case to 'approved'. Returns the new
 *  quote id so the caller can navigate / refetch. */
export async function approveCase(input: ApproveCaseInput): Promise<string> {
  const { data, error } = await supabase.rpc("approve_case_with_quote", {
    p_competition_id: input.competitionId,
    p_manual_discount_percent: input.manualDiscountPercent ?? 0,
    p_scholarship_amount_cents: input.scholarshipAmountCents ?? 0,
    p_notes_for_client: input.notesForClient ?? null,
    p_tier: input.tier ?? null,
    p_promo_code: input.promoCode ?? null,
  });
  if (error) throw new Error(error.message);
  if (typeof data !== "string") {
    throw new Error("approve_case_with_quote did not return a quote id");
  }
  return data;
}

export async function denyCase(competitionId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc("deny_case", {
    p_competition_id: competitionId,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

export async function denyCaseTerminal(competitionId: string, reason: string): Promise<void> {
  const { error } = await supabase.rpc("deny_case_terminal", {
    p_competition_id: competitionId,
    p_reason: reason,
  });
  if (error) throw new Error(error.message);
}

// ============================================================================
// Read helpers
// ============================================================================

/** Row shape returned by listPendingApprovals — only the fields the
 *  admin list view needs, so we don't pull every column. */
export type PendingCase = {
  id: string;
  name: string;
  company_id: string;
  company_name: string | null;
  engagement_model: "licensing" | "rm" | "hybrid";
  tier: CompetitionTier | null;
  status: CompetitionStatus;
  submitted_at: string | null;
  review_started_at: string | null;
  created_by: string | null;
  client_full_name: string | null;
  /** Set when an admin created this case on behalf of the client.
   *  Drives the "Coravu-Managed" visual marker + filter chip. */
  created_by_admin: string | null;
  /** Admin's full name when admin-on-behalf-of (resolved alongside the
   *  primary query so the list row can show "by Tyler"). */
  admin_creator_name: string | null;
};

/** All cases in submitted | under_review, plus the joined company + client
 *  display info. Admin RLS lets is_admin() see every competition; this
 *  query relies on that rather than a dedicated RPC. */
export async function listPendingApprovals(): Promise<PendingCase[]> {
  return listCasesByStatus(["submitted", "under_review"], "submitted_at", true);
}

/** Cases that are approved but still moving through the billing pipeline
 *  (contract not yet signed / paid). Surfaced on the case-approvals page
 *  so an approved case never becomes unreachable — in particular, if the
 *  auto-send of the DocuSign contract fails, the admin can reopen the
 *  case from this list and retry "Send contract". */
export async function listCasesInBilling(): Promise<PendingCase[]> {
  return listCasesByStatus(
    ["approved", "contract_pending", "contract_signed", "payment_pending"],
    "approved_at",
    false,
  );
}

async function listCasesByStatus(
  statuses: CompetitionStatus[],
  orderBy: string,
  ascending: boolean,
): Promise<PendingCase[]> {
  const { data, error } = await supabase
    .from("competitions")
    .select(
      `id, name, company_id, engagement_model, tier, status,
       submitted_at, review_started_at, created_by, created_by_admin,
       companies:company_id ( name ),
       client:user_profiles!competitions_created_by_fkey ( full_name )`
    )
    .in("status", statuses)
    .order(orderBy, { ascending });

  if (error) throw new Error(error.message);
  if (!data) return [];

  // Hydrate admin-creator display names in one batched call (rather than
  // joining twice on user_profiles — PostgREST disambiguation gets noisy
  // when one table is FK-joined twice via different columns).
  const adminIds = Array.from(
    new Set(
      data
        .map((r) => r.created_by_admin as string | null | undefined)
        .filter((v): v is string => typeof v === "string" && v.length > 0),
    ),
  );
  let adminNameById = new Map<string, string>();
  if (adminIds.length > 0) {
    const { data: admins } = await supabase
      .from("user_profiles")
      .select("id, full_name")
      .in("id", adminIds);
    adminNameById = new Map(
      (admins ?? []).map((a) => [a.id as string, ((a.full_name as string | null) ?? "").trim()]),
    );
  }

  return data.map((row) => {
    const company = Array.isArray(row.companies) ? row.companies[0] : row.companies;
    const client = Array.isArray(row.client) ? row.client[0] : row.client;
    const adminId = (row.created_by_admin as string | null) ?? null;
    return {
      id: row.id as string,
      name: row.name as string,
      company_id: row.company_id as string,
      company_name: (company?.name as string | undefined) ?? null,
      engagement_model: row.engagement_model as PendingCase["engagement_model"],
      tier: (row.tier as CompetitionTier | null) ?? null,
      status: row.status as CompetitionStatus,
      submitted_at: (row.submitted_at as string | null) ?? null,
      review_started_at: (row.review_started_at as string | null) ?? null,
      created_by: (row.created_by as string | null) ?? null,
      client_full_name: (client?.full_name as string | undefined) ?? null,
      created_by_admin: adminId,
      admin_creator_name: adminId ? adminNameById.get(adminId) ?? null : null,
    };
  });
}

/** Full case detail for the admin review panel. Reads competitions row
 *  + joined company + the most recent quote (if any). */
export type CaseForReview = Competition & {
  company_name: string | null;
  client_full_name: string | null;
  client_email: string | null;
  latest_quote: Quote | null;
};

export async function getCaseForReview(competitionId: string): Promise<CaseForReview | null> {
  const { data, error } = await supabase
    .from("competitions")
    .select(
      `*,
       companies:company_id ( name ),
       client:user_profiles!competitions_created_by_fkey ( full_name, email )`
    )
    .eq("id", competitionId)
    .maybeSingle();

  if (error) throw new Error(error.message);
  if (!data) return null;

  const latest = await getLatestQuote(competitionId);
  const company = Array.isArray(data.companies) ? data.companies[0] : data.companies;
  const client = Array.isArray(data.client) ? data.client[0] : data.client;
  // Strip the join keys before returning so the row is shaped like
  // Competition for downstream consumers.
  const { companies: _c, client: _cl, ...competitionRow } = data as Record<string, unknown>;
  return {
    ...(competitionRow as unknown as Competition),
    company_name: (company?.name as string | undefined) ?? null,
    client_full_name: (client?.full_name as string | undefined) ?? null,
    client_email: (client?.email as string | undefined) ?? null,
    latest_quote: latest,
  };
}

/** Contract row shape (mirrors `public.contracts`). */
export type ContractStatus = "sent" | "viewed" | "signed" | "declined" | "voided";

export type ContractRow = {
  id: string;
  competition_id: string;
  quote_id: string;
  docusign_envelope_id: string;
  signer_email: string;
  signer_name: string | null;
  status: ContractStatus;
  sent_at: string;
  viewed_at: string | null;
  signed_at: string | null;
  declined_at: string | null;
  declined_reason: string | null;
  voided_at: string | null;
  signed_pdf_url: string | null;
  created_at: string;
};

/** Most-recent contract row for a competition. Returns null if none. */
export async function getLatestContractForCase(
  competitionId: string,
): Promise<ContractRow | null> {
  const { data, error } = await supabase
    .from("contracts")
    .select("*")
    .eq("competition_id", competitionId)
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as ContractRow | null) ?? null;
}

/**
 * Invoke the `create-docusign-envelope` Edge Function. Throws with the
 * function's error message on failure. Returns the contract id +
 * envelope id on success.
 */
export async function sendContractForCase(competitionId: string): Promise<{
  contract_id: string;
  envelope_id: string;
  signer_email: string;
}> {
  const { data, error } = await supabase.functions.invoke(
    "create-docusign-envelope",
    { body: { competition_id: competitionId } },
  );
  if (error) throw new Error(error.message);
  const payload = (data ?? {}) as {
    sent?: boolean;
    already_sent?: boolean;
    envelope_id?: string;
    contract_id?: string;
    signer_email?: string;
    error?: string;
    message?: string;
  };
  if (payload.error) throw new Error(payload.message || payload.error);
  if (!payload.envelope_id || !payload.contract_id) {
    throw new Error("Edge Function returned no envelope id");
  }
  return {
    contract_id: payload.contract_id,
    envelope_id: payload.envelope_id,
    signer_email: payload.signer_email ?? "",
  };
}

// ─── Initial-payment helpers (Stripe Checkout — Prompt 6) ─────────────

export type InitialInvoiceStatus = "pending" | "paid" | "failed" | "expired" | "refunded" | "voided";

export type InitialInvoiceRow = {
  id: string;
  status: InitialInvoiceStatus;
  amount_cents: number;
  tax_cents: number;
  total_cents: number;
  hosted_invoice_url: string | null;
  receipt_url: string | null;
  paid_at: string | null;
  failed_at: string | null;
  failure_reason: string | null;
  expires_at: string | null;
  sent_at: string | null;
  created_at: string;
};

/** Most-recent kind='initial' invoice for a competition, or null. */
export async function getLatestInitialInvoiceForCase(
  competitionId: string,
): Promise<InitialInvoiceRow | null> {
  const { data, error } = await supabase
    .from("invoices")
    .select(
      "id, status, amount_cents, tax_cents, total_cents, hosted_invoice_url, receipt_url, paid_at, failed_at, failure_reason, expires_at, sent_at, created_at",
    )
    .eq("competition_id", competitionId)
    .eq("kind", "initial")
    .order("created_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as InitialInvoiceRow | null) ?? null;
}

/**
 * Invoke create-stripe-checkout to (re)issue a Stripe Checkout Session
 * for the case's initial payment. `regenerate:true` voids any existing
 * pending invoice + Stripe session before creating a new one.
 */
export async function regenerateStripeCheckoutForCase(
  competitionId: string,
): Promise<{ invoice_id: string; checkout_url: string | null; session_id: string }> {
  const { data, error } = await supabase.functions.invoke("create-stripe-checkout", {
    body: { competition_id: competitionId, regenerate: true },
  });
  if (error) throw new Error(error.message);
  const payload = (data ?? {}) as {
    invoice_id?: string;
    session_id?: string;
    checkout_url?: string | null;
    error?: string;
    message?: string;
  };
  if (payload.error) throw new Error(payload.message || payload.error);
  if (!payload.invoice_id || !payload.session_id) {
    throw new Error("Edge Function returned no invoice id");
  }
  return {
    invoice_id: payload.invoice_id,
    session_id: payload.session_id,
    checkout_url: payload.checkout_url ?? null,
  };
}

/** Most recent quote for a competition (any status). Returns null if none. */
export async function getLatestQuote(competitionId: string): Promise<Quote | null> {
  const { data, error } = await supabase
    .from("quotes")
    .select("*")
    .eq("competition_id", competitionId)
    .order("generated_at", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw new Error(error.message);
  return (data as Quote | null) ?? null;
}

/** Count of cases waiting for admin review. Used to badge the sidebar
 *  "Case Approvals" item. Cheap — Supabase returns just the count when
 *  asked with head + count: 'exact'. */
export async function countPendingApprovals(): Promise<number> {
  const { count, error } = await supabase
    .from("competitions")
    .select("id", { count: "exact", head: true })
    .in("status", ["submitted", "under_review"]);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

/** Recently decided cases — used for the "Recently decided" count tile.
 *  Includes approved, denied, denied_terminal in the last N days. */
export async function countRecentlyDecided(days = 7): Promise<number> {
  const since = new Date(Date.now() - days * 86_400_000).toISOString();
  const { count, error } = await supabase
    .from("competitions")
    .select("id", { count: "exact", head: true })
    .in("status", ["approved", "denied", "denied_terminal"])
    .or(`approved_at.gte.${since},denied_at.gte.${since}`);
  if (error) throw new Error(error.message);
  return count ?? 0;
}

// ============================================================================
// Pricing preview — mirrors the approve_case_with_quote RPC math so the
// admin can see line items update as they type. The RPC re-validates
// server-side; this is for UI feedback only.
// ============================================================================

/** All pricing rows. Tiny table; safe to fetch each time. */
export async function listPricingTiers(): Promise<PricingTier[]> {
  const { data, error } = await supabase
    .from("pricing_tiers")
    .select("*");
  if (error) throw new Error(error.message);
  return (data ?? []) as PricingTier[];
}

export type QuotePreviewInput = {
  tier: CompetitionTier | null;
  engagement: "licensing" | "rm" | "hybrid";
  scholarshipCents: number;
  manualDiscountPercent: number;
  /** 0 for MVP — Prompt 9 wires real promo lookup. */
  promotionalDiscountPercent?: number;
  pricing: PricingTier[];
};

export type QuotePreview = {
  perStudentCents: number;
  perSubmissionCents: number;
  perPresentationCents: number | null;
  flatRmCents: number | null;
  totalDiscountPercent: number;
  initialPaymentCents: number;
  /** Set when the inputs aren't yet valid for a quote (no tier, hybrid, etc). */
  error: string | null;
};

/** Apply (cents * (100 - totalDiscount)) / 100 — same int math as the RPC. */
function applyDiscountCents(cents: number, totalDiscount: number): number {
  return Math.trunc((cents * (100 - totalDiscount)) / 100);
}

export function computeQuotePreview(input: QuotePreviewInput): QuotePreview {
  const zero: QuotePreview = {
    perStudentCents: 0,
    perSubmissionCents: 0,
    perPresentationCents: null,
    flatRmCents: null,
    totalDiscountPercent: 0,
    initialPaymentCents: 0,
    error: null,
  };
  if (input.engagement === "hybrid") {
    return { ...zero, error: "Hybrid engagement is not yet supported." };
  }
  if (!input.tier) {
    return { ...zero, error: "Set the tier on the case before generating a quote." };
  }
  const row = input.pricing.find(
    (p) => p.tier === input.tier && p.engagement_model === input.engagement
  );
  if (!row) {
    return { ...zero, error: `No pricing row for tier=${input.tier} engagement=${input.engagement}.` };
  }
  const manual = Math.max(0, Math.min(100, Math.floor(input.manualDiscountPercent)));
  const promo = Math.max(0, Math.min(100, Math.floor(input.promotionalDiscountPercent ?? 0)));
  const totalDiscount = Math.min(100, manual + promo);

  const perStudent = applyDiscountCents(row.per_student_rate_cents, totalDiscount);
  const perSubmission = applyDiscountCents(row.per_submission_rate_cents, totalDiscount);
  const perPresentation =
    row.per_presentation_rate_cents == null
      ? null
      : applyDiscountCents(row.per_presentation_rate_cents, totalDiscount);
  const flatRm =
    row.flat_rm_fee_cents == null
      ? null
      : applyDiscountCents(row.flat_rm_fee_cents, totalDiscount);

  const scholarship = Math.max(0, Math.floor(input.scholarshipCents));
  const initialPayment = scholarship + (flatRm ?? 0);

  return {
    perStudentCents: perStudent,
    perSubmissionCents: perSubmission,
    perPresentationCents: perPresentation,
    flatRmCents: flatRm,
    totalDiscountPercent: totalDiscount,
    initialPaymentCents: initialPayment,
    error: null,
  };
}

/** Format cents as a USD string. "$4.99", "$5,000.00", "$0.00". */
export function formatCents(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}
